<template>
  <div class="promo-deeplink">
    <template v-if="deviceType === DefaultDeviceOptions.roku_os.value">
      <div class="deeplink-roku">
        <v-select
          v-model="getMediaType"
          variant="outlined"
          density="compact"
          bgColor="#fff"
          placeholder="Select product id"
          :items="mediaTypes"
          itemTitle="text"
          label="Media Types"
          hideDetails
        ></v-select>
        <v-text-field
          v-model.trim="getContentId"
          variant="outlined"
          persistentPlaceholder
          density="compact"
          hideDetails
          label="Content ID"
          class="roku-content-id"
          name="content_id"
          style="padding: 5px 0 2px"
        ></v-text-field>
      </div>
      <RfAdvancedArguments ref="advancedArgsBody" @update:argPairs="applyArgPairs" />
    </template>
    <div v-else>
      <v-text-field
        v-model.trim="getDeeplink"
        variant="outlined"
        persistentPlaceholder
        density="compact"
        hideDetails
        label="Deep Link"
        name="deeplink"
        style="padding: 5px 0 2px"
      ></v-text-field>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import RfAdvancedArguments from "@/blocks/RfAdvancedArguments.vue";
import { DefaultDeviceOptions } from "@/utils/constants/DevicesConstants";

export default {
  name: "RfRetentionActionsDeeplink",
  components: { RfAdvancedArguments },
  props: ["actions", "deviceType"],
  setup: () => ({ advancedArgsBody: ref(), DefaultDeviceOptions }),
  data() {
    return {
      contentId: "",
      mediaType: null,
      deeplinkHash: null,
      deeplink: "",
      mediaTypes: [
        { value: "", text: "None Selected" },
        { value: "movie", text: "Movie" },
        { value: "episode", text: "Episode" },
        { value: "season", text: "Season" },
        { value: "series", text: "Series" },
        { value: "shortFormVideo", text: "Short Form Video" },
        { value: "special", text: "Special" },
        { value: "live", text: "Live" },
      ],
      initDeepLinkHashValue: null,
    };
  },
  computed: {
    deeplinkToHash() {
      if (this.deeplinkHash) return this.deeplinkHash;
      return this.initDeepLinkHash();
    },
    getMediaType: {
      get() {
        if (this.mediaType) return this.mediaType;
        return this.deeplinkToHash.mediaType || "";
      },
      set(value) {
        this.mediaType = value;
      },
    },
    getContentId: {
      get() {
        if (this.contentId) return this.contentId;
        return this.deeplinkToHash.contentId || "";
      },
      set(value) {
        this.contentId = value;
        if (!value.length) {
          // watching content id did not react when highlighting the whole value and pressing delete, so just move logic here
          const copyHash = { ...this.deeplinkHash };
          delete copyHash.contentId;
          this.deeplinkHash = copyHash;
        } else {
          this.deeplinkHash = {
            ...this.deeplinkToHash,
            contentId: value,
          };
        }
      },
    },
    getDeeplink: {
      get() {
        if (this.deeplink) return this.deeplink;
        const deeplink = this.actions.rf_settings_deeplink;
        if (!deeplink || !deeplink.length) return null;
        return decodeURIComponent(deeplink);
      },
      set(value) {
        this.deeplink = value;
        if (!value) this.actions.rf_settings_deeplink = value; // blank values not being picked up by watcher
      },
    },
  },
  watch: {
    mediaType(mediaType) {
      this.deeplinkHash = { ...this.deeplinkToHash, mediaType };
      if (!mediaType) {
        const copyHash = { ...this.deeplinkHash };
        delete copyHash.mediaType;
        this.deeplinkHash = copyHash;
      } else {
        this.deeplinkHash = { ...this.deeplinkToHash, mediaType };
      }
    },
    deeplinkHash(to) {
      const deeplink = Object.entries(to)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");
      this.actions.rf_settings_deeplink = deeplink;
    },
    deeplink(to) {
      const [url, params] = to.split("?");
      if (params) {
        const encodedParams = params
          .split("&")
          .map(item => {
            const [k, v] = item.split("=");
            if (k && v) {
              const encodedKey = encodeURIComponent(k);
              const encodedVal = encodeURIComponent(v);
              return `${encodedKey}=${encodedVal}`;
            }
            return "";
          })
          .join("&");
        this.actions.rf_settings_deeplink = `${url}?${encodedParams}`;
      } else {
        this.actions.rf_settings_deeplink = url;
      }
    },
  },
  created() {
    this.initDeepLinkHashValue = this.initDeepLinkHash(); // have to init this value
    if (!Object.entries(this.initDeepLinkHashValue).length) return;
    if (this.deviceType !== DefaultDeviceOptions.roku_os.value) return;
    if (this.initDeepLinkHashValue.mediaType || this.initDeepLinkHashValue.contentId) {
      this.deeplinkHash = {
        ...(this.initDeepLinkHashValue.mediaType
          ? { mediaType: this.initDeepLinkHashValue.mediaType }
          : {}),
        ...(this.initDeepLinkHashValue.contentId
          ? { contentId: this.initDeepLinkHashValue.contentId }
          : {}),
      };
    }
  },
  mounted() {
    this.initAdvancedArgs();
  },
  methods: {
    initDeepLinkHash() {
      const deeplink = this.actions.rf_settings_deeplink;
      if (!deeplink?.length) return {};
      const deeplinkItems = decodeURIComponent(deeplink).split("&");
      const queryToHash = deeplinkItems.reduce((h, item) => {
        const [k, v] = item.split("=");
        if (k && v) {
          h[k] = v;
        }
        return h;
      }, {});
      return queryToHash;
    },
    applyArgPairs(argPairs) {
      this.deeplinkHash = {
        ...(this.deeplinkToHash.mediaType ? { mediaType: this.deeplinkToHash.mediaType } : {}),
        ...(this.deeplinkToHash.contentId ? { contentId: this.deeplinkToHash.contentId } : {}),
        ...argPairs,
      };
    },
    initAdvancedArgs() {
      if (this.deviceType !== DefaultDeviceOptions.roku_os.value) return;
      const hash = { ...this.initDeepLinkHashValue };
      delete hash.contentId;
      delete hash.mediaType;
      this.advancedArgsBody?.init({ ...hash });
    },
  },
};
</script>

<style lang="scss" scoped>
.deeplink-roku {
  display: flex;
  flex-direction: row;
  .roku-content-id {
    padding: 0 !important;
  }
  > div.v-input {
    flex: 1;
    padding-right: 16px;
  }
  > div.v-input:last-child {
    flex: 1;
    padding-right: 0px;
  }
}
</style>
